import { Component, computed, DestroyRef, inject, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { BsModalService } from 'ngx-bootstrap/modal';
import {
    MAX_ITEMS_IN_ROW_DESKTOP_LEFT,
    MAX_ITEMS_IN_ROW_DESKTOP_TOP,
    MAX_ITEMS_IN_ROW_MOBILE,
    MAX_PER_PAGE,
    MIN_ITEMS_IN_ROW_DESKTOP,
    MIN_ITEMS_IN_ROW_MOBILE,
    MIN_PER_PAGE,
} from '@web/widgets/catalog-products/constants/defaults-catalog-products.const';
import { type FormControl } from '@angular/forms';
import type { CatalogCategory } from '@web/widgets/product-card/type/product-card-widget.type';
import { take } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserFacade } from '@web/store/user/user.facade';
import { CatalogEnabledCategoriesService } from '@web/widgets/catalog-products/service/catalog-enabled-categories.service';
import { CommonElementsModule } from '@web/widgets/common/common-elements.module';
import { ButtonSettingsComponent } from './button-settings/button-settings.component';
import { SortingSettingsComponent } from './sorting-settings/sorting-settings.component';
import { PaginationSettingsComponent } from './pagination-settings/pagination-settings.component';
import { FilterSettingsComponent } from './filter-settings/filter-settings.component';
import { GeneralSettingsComponent } from './general-settings/general-settings.component';
import {
    CatalogCategoriesView,
    type CatalogProductsDataFormType,
    type CatalogProductsWidgetData,
    type ItemsInRowFormGroup,
} from '@web/widgets/catalog-products/type';
import { catalogProductsWidgetDataFormGroup } from '@web/widgets/catalog-products/form/catalog-products-widget.form';
import { ProductCatalogFacade } from '@web/store/website/product-catalog/product-catalog.facade';
import { getCrmPlansUrl, getCrmProductsUrl } from '@web/helpers/crm';
import {
    type EnabledCategoryIds,
    hasEnabledCategories,
    isCategoryEnabled,
} from '@web/widgets/catalog-products/utils/enabled-categories.util';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

export enum CurrentSettings {
    MAIN = 'main',
    BUTTON = 'button',
    PAGINATION = 'pagination',
    SORTING = 'sorting',
    FILTER = 'filter',
    GENERAL = 'general',
}

@Component({
    selector: 'web-product-card-control',
    templateUrl: './catalog-products-control.component.html',
    styleUrls: ['./catalog-products-control.component.less'],
    standalone: true,
    imports: [
        TranslateModule,
        CommonElementsModule,
        ButtonSettingsComponent,
        SortingSettingsComponent,
        PaginationSettingsComponent,
        FilterSettingsComponent,
        GeneralSettingsComponent,
        TooltipModule,
    ],
})
export class CatalogProductsControlComponent
    extends WebBaseControlComponent<CatalogProductsWidgetData>
    implements OnInit
{
    protected readonly modalService: BsModalService = inject(BsModalService);
    protected readonly translate: TranslateService = inject(TranslateService);
    protected readonly userFacade: UserFacade = inject(UserFacade);
    protected readonly productCatalogFacade: ProductCatalogFacade = inject(ProductCatalogFacade);
    private readonly enabledCategoriesService = inject(CatalogEnabledCategoriesService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly flattenedCategories = signal<CatalogCategory[]>([]);
    public readonly enabledCategoryIds = signal<EnabledCategoryIds>(null);
    private readonly enabledCategoriesLoaded = signal(false);
    /** The widget has nothing to show until the store publishes a category — or the list failed to load. */
    public readonly hasNoAvailableCategories = computed(
        () => this.enabledCategoriesLoaded() && !hasEnabledCategories(this.enabledCategoryIds())
    );
    private readonly userCdnInfo$ = this.userFacade.userCdnInfo$;
    public readonly currentSettings = signal<CurrentSettings>(CurrentSettings.MAIN);
    public readonly isTariffExpired = toSignal(this.productCatalogFacade.isTariffExpired$, { initialValue: false });
    public settingEnum = CurrentSettings;

    ngOnInit() {
        super.ngOnInit();

        this.userCdnInfo$.pipe(take(1)).subscribe(({ userHash }) => {
            this.userHash.patchValue(userHash);
        });

        this.loadEnabledCategoryIds();
    }

    /**
     * A parent category id is present in the enabled list only when its whole subtree is enabled,
     * so a partially enabled parent cannot be selected.
     */
    public isCategoryDisabled(category: CatalogCategory): boolean {
        return !isCategoryEnabled(category, this.enabledCategoryIds());
    }

    /** "All categories" stands for every category the store publishes, so one of them is enough. */
    public isAllCategoriesDisabled(): boolean {
        return !hasEnabledCategories(this.enabledCategoryIds());
    }

    public isCategorySelected(category: CatalogCategory): boolean {
        return category?.id === (this.selectedCategory?.value ?? null);
    }

    /** The select shows what the widget is pinned to, so «All categories» is how the pin is removed. */
    public isAllCategoriesSelected(): boolean {
        return (this.selectedCategory?.value ?? null) === null;
    }

    private loadEnabledCategoryIds(): void {
        this.enabledCategoriesService.enabledCategoryIds$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((categoryIds) => {
                this.enabledCategoryIds.set(categoryIds);
                this.enabledCategoriesLoaded.set(true);
            });
    }

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: catalogProductsWidgetDataFormGroup(),
        });
    }

    protected setFormData(): void {
        this.controlForm.patchValue({ data: this.widgetData }, { emitEvent: false });
        this.flattenedCategories.set(this.getFlattenedCategories(this.widgetData?.categories));
        if (!this.widget?.displayId) {
            this.updateWidgetDisplayId(this.displayId);
        }
    }

    public setCurrentSettingsType(val: CurrentSettings): void {
        this.currentSettings.set(val);
    }

    public setCatalogView(value: CatalogCategoriesView): void {
        this.catalogView.patchValue(value);

        if (value === CatalogCategoriesView.left) {
            this.setItemsInRowDesktop(4);
        } else {
            this.setItemsInRowDesktop(5);
        }
    }

    public setItemsInRowDesktop(value: number): void {
        this.itemsInRowDesktop.patchValue(Number(value));
    }

    public setItemsInRowMobile(value: number): void {
        this.itemsInRowMobile.patchValue(Number(value));
    }

    public setMaxPerPage(value: number): void {
        this.maxPerPage.patchValue(Number(value));
    }

    public setSelectedCategory(id: string): void {
        if (id === 'null') {
            this.selectedCategory.patchValue(null);
            return;
        }

        const category = this.flattenedCategories().find((c) => c.id === +id);
        if (category) {
            this.selectedCategory.patchValue(+category.id);
        }
    }

    public setIsShowButton(value: boolean) {
        this.basketButton.get('isShowButton').setValue(value);
    }

    public get catalogView(): CatalogProductsDataFormType['catalogView'] {
        return this.data.get('catalogView') as CatalogProductsDataFormType['catalogView'];
    }

    public get itemsInRow(): ItemsInRowFormGroup {
        return this.data.get('itemsInRow') as ItemsInRowFormGroup;
    }

    public get itemsInRowDesktop(): FormControl<number> {
        return this.itemsInRow.get('desktop') as FormControl<number>;
    }

    public get itemsInRowMobile(): FormControl<number> {
        return this.itemsInRow.get('mobile') as FormControl<number>;
    }

    public get maxPerPage(): CatalogProductsDataFormType['maxPerPage'] {
        return this.data.get('maxPerPage') as CatalogProductsDataFormType['maxPerPage'];
    }

    public get basketButton(): CatalogProductsDataFormType['basketButton'] {
        return this.data.get('basketButton') as CatalogProductsDataFormType['basketButton'];
    }

    public get selectedCategory(): CatalogProductsDataFormType['selectedCategory'] {
        return this.data.get('selectedCategory') as CatalogProductsDataFormType['selectedCategory'];
    }

    public get generalSettings(): CatalogProductsDataFormType['settings'] {
        return this.data.get('settings') as CatalogProductsDataFormType['settings'];
    }

    public get paginationSettings(): CatalogProductsDataFormType['pagination'] {
        return this.data.get('pagination') as CatalogProductsDataFormType['pagination'];
    }

    public get sortingSettings(): CatalogProductsDataFormType['sorting'] {
        return this.data.get('sorting') as CatalogProductsDataFormType['sorting'];
    }

    public get filterSettings(): CatalogProductsDataFormType['filter'] {
        return this.data.get('filter') as CatalogProductsDataFormType['filter'];
    }

    public get userHash(): CatalogProductsDataFormType['userHash'] {
        return this.data.get('userHash') as CatalogProductsDataFormType['userHash'];
    }

    public getWidgetId(widget) {
        return widget.displayId ? widget.displayId : `widget-${widget.id}`;
    }

    private getFlattenedCategories(categories: CatalogCategory[], depth = 0): CatalogCategory[] {
        if (!categories || !categories.length) {
            return [];
        }

        return categories.reduce((flattened: CatalogCategory[], category) => {
            const indentedCategory = {
                ...category,
                name: '-'.repeat(depth) + ' ' + this.translate.instant(category.name),
            };

            return [
                ...flattened,
                indentedCategory,
                ...(category.children ? this.getFlattenedCategories(category.children, depth + 1) : []),
            ];
        }, []);
    }

    public get crmUrl(): string {
        return getCrmProductsUrl();
    }

    public get crmPlansUrl(): string {
        return getCrmPlansUrl();
    }

    public get maxItemsInRow(): number {
        return this.catalogView.value === CatalogCategoriesView.left
            ? MAX_ITEMS_IN_ROW_DESKTOP_LEFT
            : MAX_ITEMS_IN_ROW_DESKTOP_TOP;
    }

    protected readonly catalogCategoriesView = CatalogCategoriesView;
    protected readonly MIN_ITEMS_IN_ROW_DESKTOP = MIN_ITEMS_IN_ROW_DESKTOP;
    protected readonly MAX_ITEMS_IN_ROW_DESKTOP_LEFT = MAX_ITEMS_IN_ROW_DESKTOP_LEFT;
    protected readonly MAX_ITEMS_IN_ROW_DESKTOP_TOP = MAX_ITEMS_IN_ROW_DESKTOP_TOP;
    protected readonly MIN_ITEMS_IN_ROW_MOBILE = MIN_ITEMS_IN_ROW_MOBILE;
    protected readonly MAX_ITEMS_IN_ROW_MOBILE = MAX_ITEMS_IN_ROW_MOBILE;
    protected readonly MIN_PER_PAGE = MIN_PER_PAGE;
    protected readonly MAX_PER_PAGE = MAX_PER_PAGE;
}
